import { doc, setDoc, getDoc, serverTimestamp, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';
import { 
  createUserWithEmailAndPassword, 
  sendPasswordResetEmail,
  deleteUser,
  getAuth,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { AppUser } from './firestore.d';

// Check if admin setup is complete
export const isAdminSetupComplete = async (): Promise<boolean> => {
  try {
    // Use a separate collection for app settings
    const adminSetupDoc = await getDoc(doc(db, 'app_settings', 'admin_setup'));
    return adminSetupDoc.exists();
  } catch (error) {
    console.error('Error checking admin setup status:', error);
    return false;
  }
};

// Create a new user
export const createUser = async (
  email: string, 
  password: string, 
  role: 'admin' | 'staff' | 'user' = 'user',
  displayName?: string,
  pinCode?: string
): Promise<string> => {
  try {
    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = userCredential.user;
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', newUser.uid), {
      uid: newUser.uid,
      email: email,
      displayName: displayName || undefined,
      role: role,
      pinCode: pinCode || undefined, // Store the PIN code if provided
      allowedHostings: ['POS'], // Default to POS (Hosting A) access
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // If creating an admin user, mark admin setup as complete
    if (role === 'admin') {
      await markAdminSetupComplete();
    }
    
    return newUser.uid;
  } catch (error: any) {
    console.error('Error creating user:', error);
    throw new Error(error.message || 'Failed to create user');
  }
};

// Mark admin setup as complete
export const markAdminSetupComplete = async (): Promise<void> => {
  try {
    // Use a separate collection for app settings
    await setDoc(doc(db, 'app_settings', 'admin_setup'), {
      setupCompleted: true,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking admin setup as complete:', error);
    throw error;
  }
};

// Send password reset email
export const resetUserPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

// Delete a user
export const deleteUserAccount = async (userId: string): Promise<void> => {
  try {
    // In a real production app, deleting a Firebase Auth user requires either:
    // 1. The user to be currently signed in (which isn't practical for admin actions)
    // 2. A Firebase Cloud Function with admin privileges
    
    // First delete from Firestore
    await deleteDoc(doc(db, 'users', userId));
    
    // For deleting the Firebase Auth account, you would need to use a Cloud Function
    // This is a placeholder for where that logic would go
    // Example Cloud Function call:
    // const functions = getFunctions();
    // const deleteAuthUser = httpsCallable(functions, 'deleteUser');
    // await deleteAuthUser({ uid: userId });
    
    console.log('User deleted successfully from Firestore');
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Update a user's PIN code
export const updateUserPinCode = async (userId: string, pinCode: string): Promise<void> => {
  try {
    // Validate PIN code format (must be 6 digits)
    if (!/^\d{6}$/.test(pinCode)) {
      throw new Error('PIN code must be a 6-digit number');
    }
    
    // Update the user document in Firestore
    await setDoc(doc(db, 'users', userId), 
      { 
        pinCode,
        updatedAt: serverTimestamp()
      }, 
      { merge: true }
    );
    
    console.log('PIN code updated successfully');
  } catch (error: any) {
    console.error('Error updating PIN code:', error);
    throw new Error(error.message || 'Failed to update PIN code');
  }
};

// Authenticate a user using their PIN code
export const authenticateWithPinCode = async (pinCode: string): Promise<AppUser | null> => {
  try {
    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    
    // Find the user with matching PIN code
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data() as AppUser;
      
      if (userData.pinCode === pinCode) {
        return userData;
      }
    }
    
    // No matching PIN found
    return null;
  } catch (error) {
    console.error('Error authenticating with PIN code:', error);
    throw error;
  }
}; 